import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

const ActivityTodoList = ({ activityIdx, todos, color = '#2196F3' }) => {
  const [states, setStates] = useState(() => 
    todos.map(() => ({ checked: false, answer: "", isCorrect: false }))
  );

  const isAnswerCorrect = (todo, answer) => {
    if (!todo.correctAnswers) return true; // Allow any answer if no validation is set
    const normalizedAnswer = answer.toLowerCase().trim();
    return todo.correctAnswers.some(correct => 
      correct.toLowerCase().trim() === normalizedAnswer
    );
  };

  const handleCheck = (idx) => {
    const todo = todos[idx];
    const currentAnswer = states[idx].answer;
    
    // If this item requires an answer, validate it
    if (todo.answer && !isAnswerCorrect(todo, currentAnswer)) {
      console.log('❌ Incorrect answer:', currentAnswer);
      return; // Don't allow checking if answer is incorrect
    }
    
    console.log('✅ Checkbox clicked! Index:', idx, 'Current checked:', states[idx]?.checked);
    
    setStates(prevStates => {
      const newStates = [...prevStates];
      newStates[idx] = { 
        ...newStates[idx], 
        checked: !newStates[idx].checked,
        isCorrect: !prevStates[idx].checked
      };
      console.log('✅ New checked state:', newStates[idx].checked);
      return newStates;
    });
  };

  const handleAnswer = (idx, answer) => {
    const todo = todos[idx];
    const isCorrect = isAnswerCorrect(todo, answer);
    
    setStates(prevStates => {
      const newStates = [...prevStates];
      newStates[idx] = { ...newStates[idx], answer, isCorrect };
      return newStates;
    });
  };

  const completedCount = states.filter(s => s.checked).length;
  const progress = Math.round((completedCount / todos.length) * 100);
  
  console.log('📊 Progress:', progress + '%', '(' + completedCount + '/' + todos.length + ')');
  return (
    <Container>
      <List>
        {todos.map((todo, idx) => (
          <TodoItem key={todo.key} $delay={idx * 0.1} $color={color}>
            <CheckRow>              <CheckboxWrapper onClick={() => handleCheck(idx)} $disabled={states[idx].answer && !states[idx].isCorrect && todos[idx].correctAnswers}>
                <StyledCheckbox $checked={states[idx].checked} $color={color} $disabled={states[idx].answer && !states[idx].isCorrect && todos[idx].correctAnswers}>
                  {states[idx].checked && (
                    <CheckIcon viewBox="0 0 24 24">
                      <polyline points="20 6 9 17 4 12" />
                    </CheckIcon>
                  )}
                </StyledCheckbox>
              </CheckboxWrapper>
              <Label $checked={states[idx].checked}>
                {todo.label}
              </Label>
            </CheckRow>            {todo.answer && (
              <AnswerWrapper>
                <AnswerInput
                  type="text"
                  value={states[idx].answer}
                  onChange={e => handleAnswer(idx, e.target.value)}
                  placeholder="Type your answer here..."
                  $checked={states[idx].checked}
                  $color={color}
                  $hasError={states[idx].answer && !states[idx].isCorrect && todo.correctAnswers}
                />
                <InputIcon $checked={states[idx].checked} $color={color} $hasError={states[idx].answer && !states[idx].isCorrect && todo.correctAnswers}>
                  {states[idx].answer && todo.correctAnswers && !states[idx].isCorrect && '✗'}
                  {states[idx].isCorrect && '✓'}
                  {!states[idx].answer && '✎'}
                </InputIcon>
                {states[idx].answer && todo.correctAnswers && !states[idx].isCorrect && (
                  <ErrorMessage>That's not quite right. Try again!</ErrorMessage>
                )}
                {states[idx].isCorrect && todo.correctAnswers && (
                  <SuccessMessage>✓ Correct!</SuccessMessage>
                )}
              </AnswerWrapper>
            )}
          </TodoItem>
        ))}
      </List>
      
      <ProgressSection>
        <ProgressHeader>
          <ProgressLabel>Progress</ProgressLabel>
          <ProgressText $color={color}>{progress}% Complete</ProgressText>
        </ProgressHeader>
        <ProgressTrack>
          <ProgressFill $width={progress} $color={color} />
        </ProgressTrack>
      </ProgressSection>
    </Container>
  );
};

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const checkmark = keyframes`
  0% {
    stroke-dashoffset: 50;
  }
  100% {
    stroke-dashoffset: 0;
  }
`;

const scaleIn = keyframes`
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
`;

const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(200%);
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  
  @media (max-width: 768px) {
    gap: 14px;
  }
  
  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  
  @media (max-width: 768px) {
    gap: 10px;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const TodoItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: rgba(255, 255, 255, 0.7);
  border: 2px solid ${props => props.$color || '#2196F3'};
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0;
  animation: ${fadeInUp} 0.6s ease-out ${props => props.$delay}s forwards;
  
  &:hover {
    transform: translateX(4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    background: rgba(255, 255, 255, 0.9);
  }
  
  @media (max-width: 768px) {
    padding: 10px 14px;
    gap: 8px;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 9px 12px;
    gap: 7px;
    border-radius: 8px;
  }
`;

const CheckRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 10px;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  flex-shrink: 0;
  user-select: none;
  opacity: ${props => props.$disabled ? 0.6 : 1};
`;

const StyledCheckbox = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 2px solid ${props => {
    if (props.$disabled) return '#f44336';
    return props.$checked ? props.$color : '#B0BEC5';
  }};
  background: ${props => {
    if (props.$disabled) return '#ffebee';
    return props.$checked ? props.$color : '#FFFFFF';
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  user-select: none;
  
  &:hover {
    transform: ${props => props.$disabled ? 'none' : 'scale(1.1)'};
    box-shadow: ${props => props.$disabled ? 'none' : `0 2px 8px ${props.$color}40`};
  }
  
  &:active {
    transform: ${props => props.$disabled ? 'none' : 'scale(0.95)'};
  }
  
  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
    border-radius: 5px;
  }
  
  @media (max-width: 480px) {
    width: 18px;
    height: 18px;
    border-radius: 4px;
  }
`;

const CheckIcon = styled.svg`
  width: 14px;
  height: 14px;
  fill: none;
  stroke: white;
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 50;
  stroke-dashoffset: 0;
  animation: ${checkmark} 0.3s ease-in-out, ${scaleIn} 0.3s ease-out;
  
  @media (max-width: 768px) {
    width: 12px;
    height: 12px;
    stroke-width: 2.2;
  }
  
  @media (max-width: 480px) {
    width: 10px;
    height: 10px;
    stroke-width: 2;
  }
`;

const Label = styled.span`
  flex: 1;
  font-size: 0.95rem;
  color: ${props => props.$checked ? "#78909C" : "#263238"};
  text-decoration: ${props => props.$checked ? "line-through" : "none"};
  font-weight: ${props => props.$checked ? 400 : 500};
  line-height: 1.5;
  transition: all 0.3s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
    line-height: 1.4;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const AnswerWrapper = styled.div`
  position: relative;
  margin-left: 34px;
  
  @media (max-width: 768px) {
    margin-left: 28px;
  }
  
  @media (max-width: 480px) {
    margin-left: 0;
  }
`;

const AnswerInput = styled.input`
  width: 100%;
  border-radius: 10px;
  border: 2px solid ${props => {
    if (props.$hasError) return '#f44336'; // Red for incorrect
    return props.$checked ? '#B0BEC5' : props.$color;
  }};
  padding: 10px 36px 10px 14px;
  font-size: 0.9rem;
  background: ${props => {
    if (props.$hasError) return '#ffebee'; // Light red background
    return props.$checked ? '#F5F5F5' : '#FFFFFF';
  }};
  color: ${props => props.$checked ? "#78909C" : "#263238"};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  
  &::placeholder {
    color: ${props => props.$checked ? "#B0BEC5" : "#90A4AE"};
    font-size: 0.85rem;
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#f44336' : props.$color};
    box-shadow: 0 0 0 3px ${props => props.$hasError ? '#f4433620' : props.$color + '20'};
    background: #FFFFFF;
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    padding: 9px 32px 9px 12px;
    border-radius: 8px;
    
    &::placeholder {
      font-size: 0.8rem;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 8px 30px 8px 10px;
    
    &::placeholder {
      font-size: 0.75rem;
    }
  }
`;

const InputIcon = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.1rem;
  color: ${props => {
    if (props.$hasError) return '#f44336'; // Red for incorrect
    return props.$checked ? '#B0BEC5' : props.$color;
  }};
  pointer-events: none;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    right: 10px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    right: 8px;
  }
`;

const ErrorMessage = styled.div`
  color: #f44336;
  font-size: 0.8rem;
  margin-top: 6px;
  font-weight: 500;
  animation: ${fadeInUp} 0.3s ease-out;
`;

const SuccessMessage = styled.div`
  color: #2E7D32;
  font-size: 0.8rem;
  margin-top: 6px;
  font-weight: 600;
  animation: ${fadeInUp} 0.3s ease-out;
`;

const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    gap: 6px;
    padding-top: 6px;
  }
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ProgressLabel = styled.span`
  font-size: 0.8rem;
  color: #546E7A;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
  }
`;

const ProgressText = styled.span`
  font-size: 0.85rem;
  color: ${props => props.$color};
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  
  @media (max-width: 768px) {
    height: 6px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    height: 5px;
    border-radius: 5px;
  }
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${props => props.$width}%;
  background: linear-gradient(90deg, ${props => props.$color} 0%, ${props => props.$color}CC 100%);
  border-radius: 8px;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 30%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4));
    animation: ${shimmer} 2s ease-in-out infinite;
  }
  
  @media (max-width: 768px) {
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    border-radius: 5px;
  }
`;

export default ActivityTodoList;